import { useState } from 'react'
import { ArrowLeft, Send, MessageCircle, Mail, Clock, Upload } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/lib/contexts/auth-context'

const supportCategories = [
  { value: 'technical', label: 'Technical Issue', description: 'Problems with features or functionality' },
  { value: 'billing', label: 'Billing & Subscription', description: 'Payment, invoices, or plan changes' },
  { value: 'account', label: 'Account & Access', description: 'Login issues or account management' },
  { value: 'feature', label: 'Feature Request', description: 'Suggest new features or improvements' },
  { value: 'other', label: 'Other', description: 'General inquiries or feedback' },
]

const priorityLevels = [
  { value: 'low', label: 'Low', description: 'General question or feedback' },
  { value: 'medium', label: 'Medium', description: 'Issue affecting productivity' },
  { value: 'high', label: 'High', description: 'Critical issue blocking work' },
]

export default function Contact() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: user?.fullName || '',
    email: user?.email || '',
    category: 'technical',
    priority: 'medium',
    subject: '',
    message: '',
    attachments: [] as File[],
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))

      toast({
        title: 'Support ticket created',
        description: 'We\'ll respond within 24 hours.',
      })

      // Reset form
      setFormData({
        ...formData,
        subject: '',
        message: '',
        attachments: [],
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to submit support ticket. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setFormData({ ...formData, attachments: [...formData.attachments, ...files] })
  }

  const removeAttachment = (index: number) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter((_, i) => i !== index),
    })
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/help">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Help Center
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Contact Support</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Get help from our support team
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Submit a Support Ticket</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you as soon as possible
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Category</Label>
                  <RadioGroup
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <div className="grid gap-3">
                      {supportCategories.map((category) => (
                        <label
                          key={category.value}
                          htmlFor={category.value}
                          className="flex items-start space-x-3 cursor-pointer"
                        >
                          <RadioGroupItem value={category.value} id={category.value} />
                          <div className="flex-1">
                            <p className="font-medium">{category.label}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {category.description}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <RadioGroup
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <div className="flex gap-4">
                      {priorityLevels.map((priority) => (
                        <label
                          key={priority.value}
                          htmlFor={`priority-${priority.value}`}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <RadioGroupItem
                            value={priority.value}
                            id={`priority-${priority.value}`}
                          />
                          <span>{priority.label}</span>
                        </label>
                      ))}
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    placeholder="Provide detailed information about your issue..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="attachments">Attachments (Optional)</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="attachments"
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('attachments')?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Add Files
                    </Button>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Max 10MB per file
                    </span>
                  </div>
                  {formData.attachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-gray-800 rounded"
                        >
                          <span>{file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Alert>
                  <AlertDescription>
                    We typically respond within 24 hours during business days. For urgent issues,
                    please select "High" priority.
                  </AlertDescription>
                </Alert>

                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    support@templatean.com
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MessageCircle className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium">Live Chat</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Available Mon-Fri, 9am-5pm EST
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium">Response Time</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Usually within 24 hours
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Before You Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Have you checked our resources?
              </p>
              <div className="space-y-2">
                <Link
                  to="/help/documentation"
                  className="block text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  → Documentation
                </Link>
                <Link
                  to="/help/faq"
                  className="block text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  → Frequently Asked Questions
                </Link>
                <Link
                  to="/help/tutorials"
                  className="block text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  → Video Tutorials
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Enterprise Support</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Enterprise customers get priority support with dedicated account managers.
              </p>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/contact-sales">
                  Contact Sales
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
